import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_URL } from '../lib/api';
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours
const TOKEN_REFRESH_THRESHOLD = 60 * 60 * 1000; // 1 hour before expiry
function decodeJWT(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const payload = JSON.parse(atob(parts[1]));
        return payload;
    }
    catch {
        return null;
    }
}
function isTokenExpired(token) {
    const decoded = decodeJWT(token);
    if (!decoded)
        return true;
    return decoded.exp * 1000 < Date.now();
}
function willTokenExpireSoon(token) {
    const decoded = decodeJWT(token);
    if (!decoded)
        return true;
    return decoded.exp * 1000 - Date.now() < TOKEN_REFRESH_THRESHOLD;
}
export const useAuthStore = create()(persist((set, get) => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    hasRehydrated: false,
    lastActiveAt: Date.now(),
    login: async (email, password) => {
        set({ isLoading: true });
        try {
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error?.message || 'Login failed');
            }
            set({
                user: data.data.user,
                token: data.data.token,
                isAuthenticated: true,
                isLoading: false,
                lastActiveAt: Date.now(),
            });
        }
        catch (error) {
            set({ isLoading: false });
            throw error;
        }
    },
    logout: () => {
        set({
            user: null,
            token: null,
            isAuthenticated: false,
            lastActiveAt: 0,
        });
    },
    checkAuth: async () => {
        const { token, lastActiveAt } = get();
        if (!token) {
            set({ isAuthenticated: false, isLoading: false });
            return;
        }
        // Check if session is older than 24 hours
        if (Date.now() - lastActiveAt > SESSION_TIMEOUT) {
            get().logout();
            return;
        }
        // Check if token is expired
        if (isTokenExpired(token)) {
            get().logout();
            return;
        }
        set({ isLoading: true });
        try {
            const response = await fetch(`${API_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (data.success) {
                set({
                    user: data.data,
                    isAuthenticated: true,
                    isLoading: false,
                    lastActiveAt: Date.now(),
                });
            }
            else {
                get().logout();
            }
        }
        catch {
            get().logout();
        }
        finally {
            set({ isLoading: false });
        }
    },
    refreshSession: async () => {
        const { token } = get();
        if (!token)
            return false;
        try {
            const response = await fetch(`${API_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (data.success) {
                set({
                    user: data.data,
                    isAuthenticated: true,
                    lastActiveAt: Date.now(),
                });
                return true;
            }
            return false;
        }
        catch {
            return false;
        }
    },
    setUser: (user) => {
        set({ user });
    },
    updateLastActive: () => {
        set({ lastActiveAt: Date.now() });
    },
    setHasRehydrated: () => {
        set({ hasRehydrated: true });
    },
}), {
    name: 'paathshaala-auth',
    partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastActiveAt: state.lastActiveAt,
    }),
    onRehydrateStorage: () => (state) => {
        if (state) {
            state.setHasRehydrated();
        }
    },
}));
// Setup activity tracker to auto-refresh session
if (typeof window !== 'undefined') {
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(event => {
        window.addEventListener(event, () => {
            const { token, isAuthenticated } = useAuthStore.getState();
            if (token && isAuthenticated) {
                useAuthStore.getState().updateLastActive();
            }
        }, { passive: true });
    });
    // Check session every 15 minutes
    setInterval(() => {
        const { token, isAuthenticated, lastActiveAt } = useAuthStore.getState();
        if (token && isAuthenticated) {
            // If inactive for more than 23 hours, try to refresh
            if (Date.now() - lastActiveAt > 23 * 60 * 60 * 1000) {
                useAuthStore.getState().refreshSession();
            }
        }
    }, 15 * 60 * 1000);
}
